// ADMIN · Overview (UI/UX audit Phase 5): every trading user at a glance - who is trading now, how much is open,
// today's P&L, loss-limit use, blocks and broker state. Read-only; GET /api/admin/overview builds each row from the
// same book the user sees on their own screens.
import React, { useCallback, useEffect, useState } from "react";
import { LayoutGrid, RefreshCw } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { inr, inrPnl } from "@/lib/format";
import { modeLabel } from "@/lib/modes";
import { visibleInterval } from "@/lib/poll";

const pnlCls = (v) => (v == null ? "text-slate-500" : v > 0 ? "text-[#00C805]" : v < 0 ? "text-[#FF6b62]" : "text-slate-300");

function Tile({ label, value, cls, testId }) {
  return (
    <div className="term-panel rounded-sm px-3 py-2" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm mono font-bold ${cls || "text-slate-100"}`}>{value}</div>
    </div>
  );
}

function Blocks({ r }) {
  const risk = r.risk || {};
  const out = [];
  if (risk.kill_switch) out.push(["KILL SWITCH", "bad"]);
  if (risk.stop_new_entries) out.push(["ENTRIES STOPPED", "warn"]);
  if (risk.recon_state && risk.recon_state !== "RECONCILED") out.push([`RECON ${risk.recon_state}`, "bad"]);
  if ((r.open || {}).exit_pending) out.push([`${r.open.exit_pending} EXITING`, "warn"]);
  if (!out.length) return <span className="text-slate-500">—</span>;
  return out.map(([t, tone]) => (
    <span key={t} className={`mr-1 text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${tone === "bad" ? "bg-[#FF3B30]/20 text-[#FF6b62]" : "bg-[#FF9500]/15 text-[#FF9500]"}`}>{t}</span>));
}

export default function AdminOverview() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get("/admin/overview").then((r) => { setD(r.data); setErr(""); })
    .catch((e) => setErr(apiError(e))), []);
  useEffect(() => { load(); return visibleInterval(load, 15000); }, [load]);
  const t = d?.totals || {};
  return (
    <div className="p-6 space-y-4" data-testid="admin-overview-page">
      <div className="flex items-center gap-3">
        <LayoutGrid className="h-6 w-6 text-[#2563EB]" />
        <div className="mr-auto">
          <h1 className="text-lg font-bold tracking-tight">Overview</h1>
          <div className="text-xs text-slate-500">Every active user: what is open now, today's P&L, limits and blocks. Read-only; refreshes every 15 s.</div>
        </div>
        <button onClick={load} className="h-8 px-3 rounded-sm border border-[#2A2F3A] text-xs inline-flex items-center gap-1" title="Refresh"><RefreshCw className="h-4 w-4" /></button>
      </div>
      {err && <div className="text-xs text-[#FF6b62]">{err}</div>}
      {!d ? <div className="text-xs text-slate-500">Loading…</div> : <>
        <div className="grid gap-1.5 grid-cols-2 sm:grid-cols-6" data-testid="overview-totals">
          <Tile label="Active users" value={t.users} />
          <Tile label="Trading now" value={t.trading_now} testId="ov-trading-now" />
          <Tile label="Open trades" value={t.open_trades} />
          <Tile label="Unrealised P&L" value={inrPnl(t.unrealised_pnl, 0)} cls={pnlCls(t.unrealised_pnl)} />
          <Tile label="Realised today" value={inrPnl(t.realised_today, 0)} cls={pnlCls(t.realised_today)} />
          <Tile label="Blocked" value={t.blocked} cls={t.blocked ? "text-[#FF6b62]" : "text-slate-100"} />
        </div>
        <div className="term-panel rounded-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="overview-table">
            <thead className="text-slate-500 border-b border-[#2A2F3A]"><tr>
              {["User", "Zerodha", "Modes", "Open trades", "Unrealised", "In use", "Realised today", "Loss limit used", "Blocks", "Broker"].map((h) =>
                <th key={h} className="text-left font-semibold px-3 py-2 whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {d.users.map((r) => {
                const o = r.open || {}, td = r.today || {}, risk = r.risk || {};
                return (
                  <tr key={r.user_id} className="gz-row" data-testid={`overview-row-${r.email}`}>
                    <td className="px-3 py-2 whitespace-nowrap"><div className="text-slate-100">{r.name}</div><div className="text-[10px] text-slate-500 mono">{r.email}</div></td>
                    <td className="px-3 py-2 mono whitespace-nowrap">{r.broker?.broker_user_id || <span className="text-slate-500">{r.note || "—"}</span>}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-[10px]">{r.modes ? <>F {modeLabel(r.modes.futures)}{r.modes.options ? <><br />O {modeLabel(r.modes.options)}</> : null}</> : "—"}</td>
                    <td className="px-3 py-2 mono">{o.trades ?? "—"}{o.live_trades ? <span className="ml-1 text-[#FF6b62] font-bold">({o.live_trades} LIVE)</span> : null}</td>
                    <td className={`px-3 py-2 mono ${pnlCls(o.unrealised_pnl)}`} title={o.unpriced ? `${o.unpriced} trade(s) without a price are not in this figure` : undefined}>
                      {o.trades ? inrPnl(o.unrealised_pnl, 0) : "—"}{o.unpriced ? <span className="ml-1 text-[#FF9500]">⚠</span> : null}</td>
                    <td className="px-3 py-2 mono">{o.trades ? inr(o.capital_in_use) : "—"}</td>
                    <td className={`px-3 py-2 mono ${pnlCls(td.realised_pnl)}`}>{td.completed ? `${inrPnl(td.realised_pnl, 0)} (${td.completed})` : "—"}</td>
                    <td className="px-3 py-2 mono" title={risk.options_daily_loss_limit ? `Options daily loss limit ${inr(risk.options_daily_loss_limit)}` : "No options daily loss limit set"}>
                      {risk.loss_limit_used_pct == null ? <span className="text-slate-500">{r.modes ? "no limit" : "—"}</span>
                        : <span className={risk.loss_limit_used_pct >= 80 ? "text-[#FF6b62] font-bold" : ""}>{risk.loss_limit_used_pct}%</span>}</td>
                    <td className="px-3 py-2 whitespace-nowrap"><Blocks r={r} /></td>
                    <td className="px-3 py-2 whitespace-nowrap text-[10px]" title={r.broker?.last_error || undefined}>
                      <span className={r.broker?.status === "CONNECTED" ? "text-[#00C805]" : "text-slate-400"}>{r.broker?.status || "—"}</span>
                      {r.broker?.last_error && <div className="text-[#FF6b62] truncate max-w-[12rem]">{r.broker.last_error}</div>}
                      {r.error && <div className="text-[#FF6b62]" data-testid="overview-row-error">figures unavailable: {r.error}</div>}</td>
                  </tr>);
              })}
            </tbody>
          </table>
        </div>
      </>}
    </div>
  );
}
